import React, { useMemo } from "react";
import { Label } from "@/components/ui/label";
import type { FormikProps } from "formik";
import { useTranslation } from "react-i18next";
import { getIn } from "formik";
import { AlertCircle } from "lucide-react";

import { BaseTypeSelect } from "./BaseTypeSelect";
import { CumulBulletinsConfig } from "./CumulBulletinsConfig";
import { CumulStockVarConfig } from "./CumulStockVarConfig";
import { ComplexExpressionBuilder } from "./ComplexExpressionBuilder";
import { QuestionnaireVariableSelect } from "./QuestionnaireVariableSelect";
import { SilaeExpressionEditor } from "./SilaeExpressionEditor";
import { SilaeVariableConfig } from "./SilaeVariableConfig";
import { EvpForceConfig } from "./EvpForceConfig";
import { SourceConditionsConfig } from "./SourceConditionsConfig";
import { FormikInput } from "@/components/FormikField";
import type { SilaeFormData, SilaeSourceFormData } from "@/types/silae";
import { createEmptySource } from "@/hooks/useSilaeWizardFormik";

// Palette par niveau d'imbrication : light sobre, dark très atténué
const COMPLEX_EXPRESSION_DEPTH_STYLES = [
  // Niveau 0 — violet
  "space-y-4 border border-violet-600  p-4 bg-violet-50 dark:border-violet-900 dark:bg-violet-950/20",
  // Niveau 1 — indigo
  "space-y-4 border border-[#4b6f8c]  p-4 bg-[#e5eeed] dark:border-[#4b6f8c] dark:bg-[#1a202c]",
  // Niveau 2 — sky (fallback pour depth >= 2)
  "space-y-4 border border-sky-600  p-4 bg-sky-100 dark:border-sky-900 dark:bg-sky-950/20",
];

/**
 * Composant pour configurer une seule source
 */
interface SourceItemConfigProps {
  formik: FormikProps<SilaeFormData>;
  sourceFieldPrefix: string; // Ex: "ifBranch.bonuses.0.bases.0"
  totalSources?: number; // Total des sources dans le même tableau (optionnel, non utilisé dans complex_expression)
  sourceIndex?: number; // Index de cette source (optionnel, non utilisé dans complex_expression)
  complexDepth?: number; // Profondeur d'imbrication dans une complex_expression (0 = racine)
}

export const SourceItemConfig: React.FC<SourceItemConfigProps> = ({
  formik,
  sourceFieldPrefix,
  totalSources,
  sourceIndex,
  complexDepth = 0,
}) => {
  const { t } = useTranslation();

  // Récupérer les valeurs de la source
  const sourceType = getIn(formik.values, `${sourceFieldPrefix}.sourceType`);
  const hasConditions = getIn(
    formik.values,
    `${sourceFieldPrefix}.hasConditions`,
  );
  const elseSource = getIn(formik.values, `${sourceFieldPrefix}.elseSource`);
  const currentSource: SilaeSourceFormData = getIn(
    formik.values,
    sourceFieldPrefix,
  );

  /**
   * Resets the entire source to a clean empty state for the new type,
   * preserving only the fields that are independent of the source type
   * (id, conditions, elseSource). This clears any lingering validation
   * errors from a previously selected type.
   */
  const handleSourceTypeChange = (newType: string) => {
    const fresh = createEmptySource();
    const reset: SilaeSourceFormData = {
      ...fresh,
      sourceType: newType as SilaeSourceFormData["sourceType"],
      // Preserve type-agnostic fields
      id: currentSource?.id ?? fresh.id,
      hasConditions: currentSource?.hasConditions ?? false,
      conditions: currentSource?.conditions ?? [],
      conditionsOperators: currentSource?.conditionsOperators ?? [],
      elseSource: currentSource?.elseSource ?? null,
      variableName: currentSource?.variableName ?? fresh.variableName,
      mathOperator: currentSource?.mathOperator ?? fresh.mathOperator,
      // Preserve cumul_stock_var defaults when switching to that type
      ...(newType === "cumul_stock_var" && {
        cumulStockVarPeriod: "year_current",
        cumulStockVarDateDebut: "Date(1,1,bul.annee)",
        cumulStockVarDateFin: "Date(31,12,bul.annee)",
      }),
    };
    formik.setFieldValue(sourceFieldPrefix, reset, true);
  };

  // Détecter si on est dans une expression complexe
  // Le chemin contient ".complexExpression." si on est dans une expression complexe
  const isInComplexExpression =
    sourceFieldPrefix.includes(".complexExpression");

  // Détecter si on est dans un elseSource
  // Le chemin contient ".elseSource" si on est dans un elseSource
  const isInElseSource = sourceFieldPrefix.includes(".elseSource");

  // Récupérer les erreurs de validation pour elseSource
  const elseSourceErrors = useMemo(
    () => getIn(formik.errors, `${sourceFieldPrefix}.elseSource`),
    [formik.errors, sourceFieldPrefix],
  );

  const hasElseSourceErrors = useMemo(() => {
    if (!hasConditions || !elseSourceErrors) return false;
    if (typeof elseSourceErrors === "string") return true;
    if (typeof elseSourceErrors === "object") {
      return Object.keys(elseSourceErrors).length > 0;
    }
    return false;
  }, [hasConditions, elseSourceErrors]);

  const shouldShowEvpForce =
    sourceType && sourceType !== "evp" && !isInComplexExpression;
  // (sourceType !== "complex_expression" || sourceIndex === 0);

  return (
    <div className="space-y-4">
      {/* Conditions de la source - Masquer si on est dans une expression complexe ou dans un elseSource */}
      {!isInComplexExpression &&
        !isInElseSource &&
        totalSources !== undefined &&
        sourceIndex !== undefined && (
          <SourceConditionsConfig
            formik={formik}
            sourceFieldPrefix={sourceFieldPrefix}
            totalSources={totalSources}
            sourceIndex={sourceIndex}
          />
        )}

      {/* Type de source */}
      <div className="space-y-2">
        <Label>{t("baseSourceConfig.sourceLabel")}</Label>
        <BaseTypeSelect
          value={sourceType || "fixed"}
          onValueChange={handleSourceTypeChange}
          error={getIn(formik.errors, `${sourceFieldPrefix}.sourceType`)}
          touched={Boolean(
            getIn(formik.touched, `${sourceFieldPrefix}.sourceType`) ||
            formik.submitCount > 0,
          )}
          disableComplexExpression={complexDepth >= 3}
        />
      </div>

      {/* Configuration selon le type */}
      {sourceType === "fixed" && (
        <FormikInput
          formik={formik}
          name={`${sourceFieldPrefix}.amount`}
          label={t("baseSourceConfig.baseAmount.label")}
          type="number"
          min="0"
          step="0.01"
          placeholder={t("baseSourceConfig.baseAmount.placeholder")}
        />
      )}

      {sourceType === "evp" && (
        <>
          <FormikInput
            formik={formik}
            name={`${sourceFieldPrefix}.evpTitle`}
            label={t("baseSourceConfig.evp.titleLabel")}
            placeholder={t("baseSourceConfig.evp.titlePlaceholder")}
            uppercase
          />
          <FormikInput
            formik={formik}
            name={`${sourceFieldPrefix}.evpDefaultValue`}
            label={t("baseSourceConfig.evp.defaultValueLabel")}
            type="number"
            min="0"
            step="0.01"
            placeholder={t("baseSourceConfig.evp.defaultValuePlaceholder")}
          />
        </>
      )}

      {(sourceType === "salary_base" ||
        sourceType === "salary_gross" ||
        sourceType === "salary_net") && (
        <FormikInput
          formik={formik}
          name={`${sourceFieldPrefix}.salaryPercentage`}
          label={
            sourceType === "salary_base"
              ? t("baseSourceConfig.salary.basePercentage")
              : sourceType === "salary_gross"
                ? t("baseSourceConfig.salary.grossPercentage")
                : t("baseSourceConfig.salary.netPercentage")
          }
          type="number"
          min="0"
          max="100"
          step="0.01"
          placeholder={t("baseSourceConfig.salary.placeholder")}
        />
      )}

      {sourceType === "stored_variable" && (
        <FormikInput
          formik={formik}
          name={`${sourceFieldPrefix}.storedVariableName`}
          label={t("baseSourceConfig.storedVariable.label")}
          placeholder={t("baseSourceConfig.storedVariable.placeholder")}
          uppercase
        />
      )}

      {sourceType === "cumul_bulletins" && (
        <div className="space-y-2">
          <Label>{t("baseSourceConfig.cumul.label")}</Label>
          <CumulBulletinsConfig
            formik={formik}
            fieldPrefix={sourceFieldPrefix}
          />
        </div>
      )}

      {sourceType === "cumul_stock_var" && (
        <div className="space-y-4 border rounded-lg p-4 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20">
          <CumulStockVarConfig
            formik={formik}
            fieldPrefix={sourceFieldPrefix}
          />
        </div>
      )}

      {sourceType === "advanced_variable" && (
        <SilaeVariableConfig
          formik={formik}
          sourceFieldPrefix={sourceFieldPrefix}
        />
      )}

      {sourceType === "complex_expression" && (
        <div
          className={
            COMPLEX_EXPRESSION_DEPTH_STYLES[
              Math.min(complexDepth, COMPLEX_EXPRESSION_DEPTH_STYLES.length - 1)
            ] + " rounded-xl"
          }
        >
          <ComplexExpressionBuilder
            formik={formik}
            fieldPrefix={`${sourceFieldPrefix}.complexExpression`}
            title={t("baseSourceConfig.complexExpression.title")}
            depth={complexDepth}
          />
        </div>
      )}

      {sourceType === "questionnaire_variable" && (
        <QuestionnaireVariableSelect
          formik={formik}
          sourceFieldPrefix={sourceFieldPrefix}
          label={t("baseSourceConfig.questionnaireVariable.label")}
          description={t("baseSourceConfig.questionnaireVariable.description")}
          showPercentage={true}
          onlyNumericTypes={true}
        />
      )}

      {sourceType === "manual_formula" && (
        <div className="space-y-4 border rounded-lg p-4 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20">
          <div className="space-y-2">
            <Label className="text-sm font-medium text-amber-900 dark:text-amber-100">
              {t("baseSourceConfig.manualFormula.label")}
            </Label>
            <p className="text-xs text-amber-700 dark:text-amber-300">
              {t("baseSourceConfig.manualFormula.description")}
            </p>
          </div>
          <SilaeExpressionEditor
            formik={formik}
            fieldPrefix={`${sourceFieldPrefix}.manualFormula`}
            placeholder={t("baseSourceConfig.manualFormula.placeholder")}
            height="300px"
            className="border border-border rounded-lg"
          />
          <p className="text-xs text-amber-600 dark:text-amber-400">
            💡 {t("baseSourceConfig.manualFormula.helpText")}
          </p>
        </div>
      )}

      {/* Forçage via EVP (disponible pour tous les types sauf "evp" et "complex_expression") */}
      {/* Ne pas afficher dans les expressions complexes imbriquées (uniquement au premier niveau) */}
      {shouldShowEvpForce && (
        <EvpForceConfig formik={formik} fieldPrefix={sourceFieldPrefix} />
      )}

      {/* Source alternative (ELSE) - Afficher si hasConditions est true */}
      {!isInComplexExpression && hasConditions && (
        <div
          className={`mt-6 space-y-4 border-2 rounded-lg p-4 bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-950/20 dark:to-amber-950/20 ${
            hasElseSourceErrors
              ? "border-red-500 dark:border-red-500"
              : "border-orange-200 dark:border-orange-800"
          }`}
        >
          <div className="flex items-center gap-2">
            <div className="px-3 py-1 bg-orange-500 text-white text-xs font-semibold rounded-full">
              {t("sourceItemConfig.elseSource.badge")}
            </div>
            <Label className="text-sm font-medium text-orange-900 dark:text-orange-100">
              {t("sourceItemConfig.elseSource.title")}
            </Label>
          </div>
          <p className="text-xs text-orange-700 dark:text-orange-300">
            {t("sourceItemConfig.elseSource.description")}
          </p>

          {/* Affichage des erreurs de validation pour elseSource */}
          {hasElseSourceErrors && (
            <div className="p-3 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  {typeof elseSourceErrors === "string" ? (
                    <p className="text-sm text-red-600 dark:text-red-400">
                      {elseSourceErrors}
                    </p>
                  ) : typeof elseSourceErrors === "object" ? (
                    <div className="space-y-1">
                      {Object.entries(
                        elseSourceErrors as Record<string, unknown>,
                      ).map(([key, err]) => {
                        if (!err) return null;
                        return (
                          <p
                            key={key}
                            className="text-sm text-red-600 dark:text-red-400"
                          >
                            <span className="font-medium">
                              {t("sourceItemConfig.elseSource.title")}:
                            </span>{" "}
                            {key}: {String(err)}
                          </p>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-red-600 dark:text-red-400">
                      {String(elseSourceErrors)}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {elseSource ? (
            <SourceItemConfig
              formik={formik}
              sourceFieldPrefix={`${sourceFieldPrefix}.elseSource`}
              totalSources={totalSources}
              sourceIndex={sourceIndex}
            />
          ) : (
            <div className="text-center py-4">
              <button
                type="button"
                onClick={() => {
                  formik.setFieldValue(
                    `${sourceFieldPrefix}.elseSource`,
                    createEmptySource(),
                  );
                }}
                className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-md text-sm font-medium transition-colors"
              >
                {t("sourceItemConfig.elseSource.addButton")}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
